import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useIsLicensed } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
/**
 * Eligibility hook for optional nth user guided tour experiment with following requirements to be eligible
 * Desktop only
 * Licensed user
 * Not founding admin
 */
export const useIsEligibleForOptionalNthUserGuidedTour = () => {
	const isNav4Enabled = useIsNav4Enabled();
	const isLicensedUser = useIsLicensed();
	const isDesktopUser = !(isMobileUserAgent() || shouldShowMobileWeb());

	// Gate the onboarding state query by mandatory conditions
	const shouldFetchOnboardingState =
		(fg('cc_engagement_optional_guided_tour_for_nth_users') ||
			fg('cc_engagement_optional_guided_tour_for_n_users_aa')) &&
		isLicensedUser;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN],
		!shouldFetchOnboardingState,
	);

	// Deserialize all values from the combined onboarding state
	const { isFoundingAdmin } = deserializeState(onboardingStateData);

	const isNthUser =
		isLicensedUser && !onboardingStateLoading && !onboardingStateError && !isFoundingAdmin;
	const potentiallyEligibleUserForTour = isDesktopUser && isNav4Enabled && isNthUser;
	const isLoading = shouldFetchOnboardingState && onboardingStateLoading;

	if (isLoading) {
		return {
			isEligibleForTour: false,
			loading: true,
			error: false,
		};
	}

	const hasError = shouldFetchOnboardingState && onboardingStateError;
	if (hasError) {
		if (isUnauthorizedError(onboardingStateError)) {
			markErrorAsHandled(onboardingStateError);
		}
		return {
			isEligibleForTour: false,
			loading: false,
			error: onboardingStateError,
		};
	}
	return {
		isEligibleForTour: potentiallyEligibleUserForTour,
		loading: false,
		error: false,
	};
};
